import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  TRANSLATIONS,
} from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { Venue } from '../../_shared/models/csTypes/venue';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  filter,
  from,
  iif,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Court } from '../../_shared/models/csTypes/court';
import { AsyncPipe } from '@angular/common';
import { VenuesService } from '../../venues/venues.service';
import { VenueVideoBoardsService } from '../../venues/venue-details/venue-video-boards/venue-video-boards.service';
import { Match } from '../../_shared/models/match.model';
import {
  MatchAssignments,
  MatchService,
} from '../../_core/services/match.service';
import { VideoBoardAuthService } from '../../video-board-auth/video-board-auth.service';
import { ChangeDetectorRef } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { TranslateModule } from '@ngx-translate/core';
import { Preferences } from '@capacitor/preferences';

interface CourtSelector {
  venue: FormControl<Venue | null>;
  court: FormControl<Court | null>;
}

@Component({
  selector: 'app-assign-video-board',
  templateUrl: './assign-video-board.component.html',
  styleUrls: ['./assign-video-board.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, ReactiveFormsModule, AsyncPipe, TranslateModule],
})
export class AssignVideoBoardComponent implements OnDestroy {
  venueService = inject(VenuesService);
  matchService = inject(MatchService);
  venueVideoBoardsService = inject(VenueVideoBoardsService);
  modalController = inject(ModalController);
  videoBoardAuthService = inject(VideoBoardAuthService);

  @Input({ required: true }) venues: Venue[] = [];
  @Input({ required: true }) match!: Match;
  @Output() videoBoards = new EventEmitter<string[] | null>();

  assignVideoBoardForm = new FormGroup<CourtSelector>({
    venue: new FormControl(null, Validators.required),
    court: new FormControl(null, Validators.required),
  });

  venues$: Observable<Venue[]> = of(this.venues).pipe(
    switchMap((venues) => {
      return iif(() => venues.length > 0, venues, this.venueService.myVenues());
    })
  );

  courts$: Observable<Court[]> =
    this.assignVideoBoardForm.controls.venue.valueChanges.pipe(
      filter((venue) => !!venue),
      map((venue) => venue.courts)
    );

  videoBoardsToAssign: string[] = [];
  attachedVideoBoards: any[] = [];

  private readonly destroy$: Subject<void> = new Subject();

  constructor(private cdr: ChangeDetectorRef,private socket: Socket) {}

  ngOnInit() {
    this.fetchVideoBoardsByMatch();
  }

  async ngOnDestroy() {
    // Remove all listeners for the 'update-match' event
    this.socket.removeAllListeners('update-match');
    this.destroy$.next();
    this.destroy$.complete();
  }

  async modalDissmissed() {
    await this.modalController.dismiss();
  }

  selectVideoBoards(ev, _videoBoardId: string) {
    if (ev.detail.checked) {
      this.videoBoardsToAssign.push(_videoBoardId);
    } else {
      const index = this.videoBoardsToAssign.findIndex(
        (videoBoardId) => videoBoardId === _videoBoardId
      );
      if (index > -1) {
        this.videoBoardsToAssign.splice(index, 1);
      }
    }
  }

  assignSmartCourt() {
    if (this.assignVideoBoardForm.invalid) {
      throw 'invalid';
    }

    const MatchAssignments: MatchAssignments = {
      _videoBoardIds: this.videoBoardsToAssign,
      venue: this.assignVideoBoardForm.controls.venue.value,
      court: this.assignVideoBoardForm.controls.court.value,
    };

    this.matchService
      .matchAssignments(this.match._id, MatchAssignments)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (match: any) => {
        let deviceId = (await Preferences.get({ key: 'deviceId' }))?.value;
        this.socket.emit('update-match', {matchUp: match, deviceId: deviceId});
        await this.modalController.dismiss(match);
      });
  }

  getVideoBoardToggleValue(_videoBoardId: string): boolean {
    return this.match.videoBoards.some((vb) => vb._id === _videoBoardId);
  }

  /**
   * Fetches video boards associated with the given match ID.
   */
  fetchVideoBoardsByMatch() {
    this.videoBoardAuthService
      .getVideoBoardsByMatch(this.match._id)
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          if (response && response.videoBoards) {
            this.attachedVideoBoards = response.videoBoards;
          }
        })
      )
      .subscribe();
  }

  // Removes a video board from the assigned list and updates the match
  removeVideoBoard(videoBoardId: string) {
    this.videoBoardAuthService
      .updateMatchWithVideoBoard(videoBoardId, null)
      .subscribe(() => {
        // Remove the video board from the local state after successful update
        this.attachedVideoBoards = this.attachedVideoBoards.filter(
          (videoBoard: any) => videoBoard._id !== videoBoardId
        );
        this.socket.removeAllListeners('update-match');
        this.cdr.detectChanges();
      });
  }
}
